import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { Check, Crown, Calendar, Shield, BarChart3, Users, History } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export const Subscription: React.FC = () => {
  const { user } = useAuth();
  const { success } = useNotifications();
  const [searchParams] = useSearchParams();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  
  const {
    subscriptionData,
    isLoading,
    createCheckout,
    openCustomerPortal,
    hasAccess,
    isTrialActive,
    isSubscribedUser,
    checkSubscription
  } = useSubscription();

  // Handle success/cancel from Stripe redirects
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      success({
        title: 'Abonnement activé !',
        description: 'Votre abonnement a été créé avec succès.'
      });
      checkSubscription();
    }
  }, [searchParams, success, checkSubscription]);

  const features = [
    { icon: BarChart3, text: 'Dashboard complet avec statistiques' },
    { icon: Shield, text: 'Gestion des stocks en temps réel' },
    { icon: History, text: 'Historique des ventes détaillé' },
    { icon: Crown, text: 'Export des données' },
    { icon: Calendar, text: 'Rapports mensuels automatiques' }
  ];

  const renderSubscriptionStatus = () => {
    if (isLoading) {
      return <LoadingSpinner message="Vérification du statut..." />;
    }

    if (isTrialActive) {
      const trialDaysLeft = subscriptionData?.trial_days_left ?? 0;
      
      return (
        <Card className="border-primary bg-gradient-to-r from-primary/5 to-primary/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-primary" />
              <CardTitle className="text-primary">
                {trialDaysLeft > 0 ? 'Essai démo actif' : 'Essai démo terminé'}
              </CardTitle>
            </div>
            <CardDescription>
              {trialDaysLeft > 0 
                ? 'Profitez de toutes les fonctionnalités Starter'
                : 'Souscrivez maintenant pour continuer à utiliser toutes les fonctionnalités'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {trialDaysLeft > 0 ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Jours restants</span>
                    <Badge variant="secondary" className="bg-primary/10 text-primary">
                      {trialDaysLeft} jours
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Fin de l'essai</span>
                    <span className="text-sm font-medium">
                      {subscriptionData?.trial_end ? 
                        new Date(subscriptionData.trial_end).toLocaleDateString('fr-FR') : 
                        'Non définie'
                      }
                    </span>
                  </div>
                </>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    Votre période d'essai est terminée. Souscrivez pour continuer.
                  </p>
                </div>
              )}
              <Button 
                onClick={createCheckout}
                disabled={isLoading}
                className="w-full bg-gradient-primary hover:shadow-elegant"
              >
                Souscrire maintenant - 39,90€/mois
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                {trialDaysLeft > 0 
                  ? 'Continuez avec l\'abonnement Starter après votre essai'
                  : 'Aucun engagement • Annulation à tout moment'
                }
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (isSubscribedUser) {
      return (
        <Card className="border-green-200 bg-gradient-to-r from-green-50 to-green-100">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-green-600" />
              <CardTitle className="text-green-800">Abonnement actif</CardTitle>
            </div>
            <CardDescription className="text-green-700">
              Vous avez accès à toutes les fonctionnalités Starter
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-green-700">Plan actuel</span>
                <Badge className="bg-green-600 hover:bg-green-700">
                  Starter - 39,90€/mois
                </Badge>
              </div>
              {subscriptionData?.subscription_end && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-green-700">Prochaine facturation</span>
                  <span className="text-sm font-medium text-green-800">
                    {new Date(subscriptionData.subscription_end).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
              <Button 
                onClick={openCustomerPortal}
                disabled={isLoading}
                variant="outline"
                className="w-full border-green-300 text-green-700 hover:bg-green-50"
              >
                Gérer mon abonnement
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>Débloquez toutes les fonctionnalités</CardTitle>
          <CardDescription>
            Commencez votre essai démo de 14 jours, puis continuez pour seulement 39,90€/mois
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-1">39,90€</div>
              <div className="text-sm text-muted-foreground">par mois • TTC</div>
              <div className="text-sm text-green-600 font-medium mt-1">
                Essai démo de 14 jours inclus
              </div>
            </div>
            <Button 
              onClick={async () => {
                if (!user) return;
                
                try {
                  setIsLocalLoading(true);
                  
                  const { data, error } = await supabase.functions.invoke('create-checkout');
                  
                  if (error) {
                    console.error('[SUBSCRIPTION-UI] Edge function error:', error);
                    throw error;
                  }
                  
                  if (data?.url) {
                    // Redirect to checkout - use location.href for better compatibility
                    window.location.href = data.url;
                  } else {
                    throw new Error('Aucune URL de checkout reçue');
                  }
                } catch (err) {
                  console.error('[SUBSCRIPTION-UI] Checkout failed:', err);
                  
                  const errorMessage = err?.message || 'Erreur inconnue';
                  toast({
                    title: "Erreur de paiement",
                    description: `Impossible de créer la session de paiement: ${errorMessage}`,
                    variant: "destructive"
                  });
                } finally {
                  setIsLocalLoading(false);
                }
              }}
              disabled={isLocalLoading}
              className="w-full bg-gradient-primary hover:shadow-elegant"
              size="lg"
            >
              {isLocalLoading ? 'Création...' : 'Démarrer l\'essai démo - 39,90€/mois'}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Aucun engagement • Annulation à tout moment
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        
        <main className="flex-1 flex flex-col">
          <header className="border-b border-border bg-card">
            <div className="flex items-center justify-between p-4 lg:p-6">
              <div className="flex items-center gap-4">
                <SidebarTrigger className="lg:hidden" />
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    Abonnement
                  </h1>
                  <p className="text-muted-foreground">
                    Gérez votre abonnement et accédez aux fonctionnalités Starter
                  </p>
                </div>
              </div>
            </div>
          </header>

          <div className="flex-1 p-4 lg:p-6 space-y-6 overflow-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Status */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Statut de l'abonnement</h2>
                {renderSubscriptionStatus()}
              </div>

              {/* Features */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Fonctionnalités incluses</h2>
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      {features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <feature.icon className="w-4 h-4 text-primary" />
                            </div>
                          </div>
                          <span className="text-sm text-foreground">{feature.text}</span>
                          <Check className="w-4 h-4 text-green-500 ml-auto" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Access Status */}
            {!hasAccess && (
              <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-amber-600" />
                    <div>
                      <h3 className="font-medium text-amber-800">Accès limité</h3>
                      <p className="text-sm text-amber-700">
                        Souscrivez à un abonnement pour débloquer toutes les fonctionnalités
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <footer className="border-t border-border bg-card p-4 text-center">
            <p className="text-sm text-muted-foreground">
              Des questions ? Contactez notre support à support@chart-flow.com
            </p>
          </footer>
        </main>
      </div>
    </SidebarProvider>
  );
};